(() => {
  if (window.analogueSearchLoaded) return;
  window.analogueSearchLoaded = true;
  const cache = new Map();
  let counter = 0;
  const root = window.Shopify?.routes?.root || '/';
  // Narrow spelling normalization preserves the customer's original text.
  const normalize = value => value.trim()
    .replace(/[µμ]/g, 'mju')
    .replace(/\bmju\s*[- ]?\s*2\b/gi, 'mju ii')
    .replace(/\b35\s+mm\b/gi, '35mm')
    .replace(/\bae\s*[- ]?\s*1\b/gi, 'AE-1')
    .replace(/\bpoint[ -]+(?:and[ -]+|&[ -]*)shoot\b/gi, 'point-and-shoot');
  function init() {
    document.querySelectorAll('form[role="search"] input[name="q"]:not([type="hidden"])').forEach(input => {
      if (input.dataset.analogueSearch) return;
      input.dataset.analogueSearch = 'true';
      const form = input.form;
      // The results page autofocuses its populated field after navigation.
      // Keep suggestions closed until the customer edits that submitted query.
      let awaitingResultsEdit = Boolean(form.closest('main') &&
        /\/search\/?$/.test(window.location.pathname) &&
        new URLSearchParams(window.location.search).has('q'));
      form.classList.add('analogue-search');
      const id = `analogue-search-${++counter}`;
      const label = form.querySelector(`label[for="${input.id}"]`);
      input.id = `${id}-input`;
      if (label) label.htmlFor = input.id;
      const panel = document.createElement('div');
      panel.id = id;
      panel.className = 'analogue-search__panel';
      panel.hidden = true;
      panel.setAttribute('role', 'listbox');
      panel.setAttribute('aria-label', input.getAttribute('aria-label') || 'Search');
      const layer = document.createElement('div');
      layer.className = 'analogue-search__layer';
      layer.hidden = true;
      const backdrop = document.createElement('div');
      backdrop.className = 'analogue-search__backdrop';
      backdrop.setAttribute('aria-hidden', 'true');
      layer.append(backdrop, panel);
      const status = document.createElement('span');
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      form.append(layer, status);
      Object.entries({role:'combobox', 'aria-autocomplete':'list', 'aria-controls':id, 'aria-expanded':'false', autocomplete:'off'}).forEach(([key,value]) => input.setAttribute(key,value));
      let timer, controller, hideTimer, openFrame, version = 0, active = -1, composing = false;
      const anchor = form.closest('[data-header-search]') || form.parentElement;
      const position = () => {
        if (layer.hidden) return;
        const rect = form.getBoundingClientRect();
        const boundary = anchor.getBoundingClientRect();
        const viewport = window.visualViewport;
        const available = Math.max(0, (viewport ? viewport.offsetTop + viewport.height : window.innerHeight) - boundary.bottom);
        layer.style.left = `${-rect.left}px`;
        layer.style.top = `${boundary.bottom - rect.top}px`;
        layer.style.width = `${document.documentElement.clientWidth}px`;
        layer.style.height = `${available}px`;
        layer.style.setProperty('--analogue-search-available', `${Math.max(0, available - 64)}px`);
      };
      const close = () => {
        cancelAnimationFrame(openFrame);
        clearTimeout(hideTimer);
        layer.classList.remove('is-open');
        panel.inert = true;
        input.setAttribute('aria-expanded','false');
        input.removeAttribute('aria-activedescendant');
        active = -1;
        status.textContent = '';
        hideTimer = setTimeout(() => { layer.hidden = true; panel.hidden = true; }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 340);
      };
      const cancel = () => { clearTimeout(timer); controller?.abort(); version++; input.removeAttribute('aria-busy'); };
      const show = html => {
        if (document.activeElement !== input) return;
        clearTimeout(hideTimer);
        cancelAnimationFrame(openFrame);
        const opening = !layer.classList.contains('is-open');
        panel.innerHTML = html;
        active = -1;
        input.removeAttribute('aria-activedescendant');
        panel.querySelectorAll('[data-search-option]').forEach((el,i) => { el.id = `${id}-${i}`; el.setAttribute('role','option'); el.setAttribute('aria-selected','false'); el.tabIndex = -1; });
        panel.hidden = false;
        panel.inert = false;
        layer.hidden = false;
        position();
        if (opening) {
          // Establish the filter panel's starting state before transitioning.
          void panel.offsetHeight;
          openFrame = requestAnimationFrame(() => layer.classList.add('is-open'));
        }
        input.setAttribute('aria-expanded','true');
        status.textContent = panel.querySelector('[data-search-status]')?.dataset.searchStatus || '';
      };
      async function search(value, token) {
        const query = normalize(value);
        if (cache.has(query)) { show(cache.get(query)); return; }
        controller = new AbortController();
        input.setAttribute('aria-busy','true');
        const params = new URLSearchParams({q:query, section_id:'analogue-predictive-search', 'resources[type]':'product,collection,query', 'resources[limit]':'6', 'resources[limit_scope]':'each', 'resources[options][unavailable_products]':'last'});
        try {
          let response = await fetch(`${root}search/suggest?${params}`, {signal:controller.signal});
          // Unsupported locales use Shopify's regular, translated search results.
          if (response.status === 417) {
            const fallback = new URLSearchParams({q:query, type:'product', section_id:'analogue-predictive-search', 'options[prefix]':'last', 'options[unavailable_products]':'last'});
            response = await fetch(`${root}search?${fallback}`, {signal:controller.signal});
          }
          if (!response.ok) throw new Error(`Search ${response.status}`);
          const doc = new DOMParser().parseFromString(await response.text(),'text/html');
          const results = doc.querySelector('[data-analogue-search-results]');
          if (!results) throw new Error('Missing search results');
          if (token !== version) return;
          if (cache.size >= 30) cache.delete(cache.keys().next().value);
          cache.set(query, results.innerHTML);
          show(results.innerHTML);
        } catch (error) {
          if (token === version && error.name !== 'AbortError') close();
        } finally { if (token === version) input.removeAttribute('aria-busy'); }
      }
      const schedule = () => {
        cancel();
        if (awaitingResultsEdit) { close(); return; }
        if (!composing && input.value.trim().length >= 2) {
          // Keep the backdrop stable while typing; stale rows cannot be selected.
          panel.inert = true;
          active = -1;
          input.removeAttribute('aria-activedescendant');
          const token = version;
          timer = setTimeout(() => search(input.value,token),200);
        } else close();
      };
      input.addEventListener('input', () => { awaitingResultsEdit = false; schedule(); });
      input.addEventListener('focus', schedule);
      input.addEventListener('compositionstart', () => { composing = true; cancel(); close(); });
      input.addEventListener('compositionend', () => { composing = false; awaitingResultsEdit = false; schedule(); });
      input.addEventListener('keydown', event => {
        if (event.isComposing) return;
        if (event.key === 'Escape') { cancel(); close(); return; }
        if (panel.hidden || panel.inert) return;
        const options = [...panel.querySelectorAll('[data-search-option]')];
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          active = active < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options.forEach((el,i) => el.setAttribute('aria-selected',String(i === active)));
          input.setAttribute('aria-activedescendant',options[active].id);
          options[active].scrollIntoView({block:'nearest'});
        } else if (event.key === 'Enter' && active >= 0) { event.preventDefault(); options[active].click(); }
        else if (event.key === 'Tab') { cancel(); close(); }
      });
      panel.addEventListener('mousedown', event => event.preventDefault());
      backdrop.addEventListener('pointerdown', event => { event.preventDefault(); cancel(); close(); });
      window.addEventListener('resize', position);
      window.addEventListener('scroll', position, {capture:true, passive:true});
      window.visualViewport?.addEventListener('resize', position);
      new ResizeObserver(position).observe(anchor);
      form.addEventListener('focusout', event => { if (!form.contains(event.relatedTarget)) { cancel(); close(); } });
      document.addEventListener('pointerdown', event => { if (!form.contains(event.target)) { cancel(); close(); } });
      form.addEventListener('submit', () => { awaitingResultsEdit = true; input.value = normalize(input.value); cancel(); close(); });
    });
  }
  init();
  document.addEventListener('shopify:section:load',init);
})();
